import React, { useState, useEffect } from 'react';
import { Box, LinearProgress, Typography, Paper, CircularProgress } from '@mui/material';

/**
 * Simplified LoadingOverlay component that displays basic progress information
 * 
 * @param {boolean} isLoading - Whether the overlay should be shown
 * @param {string} message - Default message to display if no progress data is available
 * @param {object} progress - Progress data object with status, message, progress percentage
 */
const LoadingOverlay = ({ isLoading = true, message = 'Processing...', progress = null, title = 'Filter Processing' }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  
  // Cool loading messages for upload
  const uploadMessages = [
    "Crunching your data...",
    "Making magic happen ✨", 
    "Processing like a boss...",
    "Almost there, hang tight!",
    "Doing the heavy lifting...",
    "Creating something amazing...",
    "Working our magic...",
    "Loading with style...",
    "Making it happen...",
    "Just a sec, we got this!"
  ];
  
  // Rotate messages every 2 seconds for upload processing
  useEffect(() => {
    let interval;
    if (isLoading && title === "Upload Processing") {
      interval = setInterval(() => {
        setCurrentMessageIndex(prev => (prev + 1) % uploadMessages.length);
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading, title, uploadMessages.length]);
  
  if (!isLoading) return null;
  
  // Extract data from progress object
  const overallProgress = progress?.progress || 0;
  const isUploadProcessing = title === "Upload Processing";
  const statusMessage = isUploadProcessing ? uploadMessages[currentMessageIndex] : (progress?.message || message);
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 1000,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          width: '400px',
          maxWidth: '90%',
          p: 3,
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        {isUploadProcessing ? (
          /* Cool upload processing display */
          <>
            <CircularProgress 
              size={60} 
              sx={{ 
                mb: 2,
                color: '#0095FF' // Wyspur blue
              }} 
            />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
              {statusMessage}
            </Typography>
          </>
        ) : (
          /* Regular filter processing display */
          <>
            <Typography variant="h6" gutterBottom>
              {title}: {Math.round(overallProgress)}%
            </Typography>
            
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress 
                variant="determinate"
                value={overallProgress}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
            
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {statusMessage}
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LoadingOverlay;
